<template>
  <div class="message-text__wrap" :class="attachmentTypeClasses">
    <img
      v-if="isImage && !isImageError"
      :src="attachment.data_url"
      @click="onClick"
      @error="onImgError()"
    />
    <video
      v-if="isVideo"
      :src="attachment.data_url"
      muted
      playsInline
      @click="onClick"
    />
    <audio v-else-if="isAudio" controls class="skip-context-menu">
      <source :src="`${attachment.data_url}?t=${Date.now()}`" />
    </audio>
    <!-- GalleryView (primary stage) -->
    <gallery-view
      v-if="show"
      :show.sync="show"
      :attachment="attachment"
      :all-attachments="filteredCurrentChatAttachments"
      @error="handleStage1Error"
      @close="onClose"
    />
    <!-- Simple modal fallback -->
    <div v-if="simpleModal" class="simple-modal">
      <div class="zoom-controls">
        <button @click.stop="zoomIn">+</button>
        <button @click.stop="zoomOut">−</button>
        <button @click.stop="resetZoom">Reset</button>
        <button @click.stop="closeSimpleModal">×</button>
      </div>
      <div class="simple-modal-content" @click="closeSimpleModal">
        <img
          :src="attachment.data_url"
          alt="expanded image"
          :style="zoomStyle"
        />
      </div>
    </div>
    <!-- Inline fallback -->
    <div v-if="inlineFallback" class="inline-fallback">
      <img
        :src="attachment.data_url"
        alt="fallback attachment"
        style="max-width: 100%; max-height: 400px; cursor: zoom-in"
        @click="openInNewTab"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { hasPressedCommand } from 'shared/helpers/KeyboardHelpers';

import GalleryView from '../components/GalleryView';

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
};

export default {
  components: {
    GalleryView,
  },
  props: {
    attachment: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      show: false,
      simpleModal: false,
      zoom: 1,
      isImageError: false,
      inlineFallback: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChatAttachments: 'getSelectedChatAttachments',
    }),
    isImage() {
      return this.attachment.file_type === ALLOWED_FILE_TYPES.IMAGE;
    },
    isVideo() {
      return this.attachment.file_type === ALLOWED_FILE_TYPES.VIDEO;
    },
    isAudio() {
      return this.attachment.file_type === ALLOWED_FILE_TYPES.AUDIO;
    },
    attachmentTypeClasses() {
      return {
        image: this.isImage,
        video: this.isVideo,
      };
    },
    filteredCurrentChatAttachments() {
      return this.currentChatAttachments.filter(attachment =>
        ['image', 'video', 'audio'].includes(attachment.file_type)
      );
    },
    zoomStyle() {
      return {
        transform: `scale(${this.zoom})`,
        transition: 'transform 0.2s ease',
        'max-width': '90%',
        'max-height': '90%',
        'object-fit': 'contain',
      };
    },
  },
  watch: {
    attachment() {
      this.isImageError = false;
      this.inlineFallback = false;
      this.simpleModal = false;
      this.zoom = 1;
    },
  },
  methods: {
    onClose() {
      this.show = false;
    },
    onClick(e) {
      if (hasPressedCommand(e)) {
        this.openInNewTab();
        return;
      }
      this.show = true;
    },
    handleStage1Error(err) {
      if (err) {
        console.error('[ImageAudioVideo] Stage1 error (gallery):', err);
      }
      this.simpleModal = true;
    },
    handleStage2Error(err) {
      if (err) {
        console.error('[ImageAudioVideo] Stage2 error (inline):', err);
      }
      this.openInNewTab();
    },
    openInNewTab() {
      if (this.attachment && this.attachment.data_url) {
        window.open(this.attachment.data_url, '_blank');
        console.info('[ImageAudioVideo] Final fallback: opened in new tab.');
      } else {
        console.warn('[ImageAudioVideo] No data_url to open.');
      }
    },
    onImgError() {
      this.isImageError = true;
      console.warn(`[ImageAudioVideo] Attachment failed to load.`, {
        fileType: this.attachment.file_type,
        url: this.attachment.data_url,
        attachmentId: this.attachment.id,
      });
    },
    zoomIn() {
      this.zoom += 0.2;
    },
    zoomOut() {
      if (this.zoom > 0.4) this.zoom -= 0.2;
    },
    resetZoom() {
      this.zoom = 1;
    },
    closeSimpleModal() {
      this.simpleModal = false;
      this.zoom = 1;
    },
  },
};
</script>

<style scoped>
.inline-fallback {
  margin-top: 8px;
  border: 1px dashed #ccc;
  padding: 8px;
  text-align: center;
  background: #fafafa;
}

.simple-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  flex-direction: column;
}

.simple-modal-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.zoom-controls {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
}

.zoom-controls button {
  background: #fff;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
</style>
